<template>
    <div class="ls-flow--container">
        <LsTemplatedLoading :show="loading" :full-screen="true" test-id="loading" />
        <div v-if="!loading" test-id="form_wrapper">
            <div class="ls-flow--title">Confirm Pause</div>

            <form novalidate @submit.prevent="createSchedulePause">
                <LsInfoCard v-if="schedulePause.contractor_schedule_pause_recommended">
                    <span class="ls-pause-view--info-card"
                        >The provider requested to pause this service type recently, so lawn mowing
                        can be paused within 48 hours of the upcoming service.</span
                    >
                </LsInfoCard>

                <div>
                    <p>Upcoming service that will be cancelled</p>
                    <p class="ls-flow--date-range">{{ upcomingRange }}</p>
                </div>

                <div>
                    <p>Reactivation service scheduled on</p>
                    <p class="ls-flow--date-range">{{ reactivationRange }}</p>
                </div>

                <LsInput
                    v-model="zendesk_ticket_id"
                    track-id="pause_view-zendesk_ticket_id"
                    label="Zendesk ticket number"
                    name="zendesk_ticket_id"
                    type="number"
                    placeholder="Enter number"
                    :validation="{ required: true, min_value: 1000000, max: 8 }"
                />

                <LsError v-if="isInvalid" />

                <LsButton
                    track-id="pause_view-confirm_pause"
                    text="Confirm Pause"
                    :loading="isUpdating"
                    type="submit"
                />
            </form>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import { lsErrorService, lsModalService } from 'ls-vue-common';
import gql from 'graphql-tag';

import { apolloClient } from '@/vue-apollo';
import { lsHelpers } from '@/helpers';
import { LsButton, LsInput, LsInfoCard } from '@/components/common';
import LsTemplatedLoading from '@/components/LsTemplatedLoading/LsTemplatedLoading.vue';
import LsError from '@/components/common/LsError/LsError.vue';
import { FlowSuccessModal } from '@/modals';
import SchedulePause from '@/graphql/queries/SchedulePause.gql';
import CreateSchedulePause from '@/graphql/mutations/CreateSchedulePause.gql';

export default {
    name: 'PauseView',
    components: {
        LsButton,
        LsInput,
        LsTemplatedLoading,
        LsError,
        LsInfoCard,
    },
    $_veeValidate: {
        validator: 'new',
    },
    data() {
        return {
            loading: 0,
            isUpdating: false,
            isInvalid: false,

            schedulePause: {},
            property: apolloClient.readFragment({
                id: `Property:${this.$route.params.property_id}`,
                fragment: gql`
                    fragment property on Property {
                        street
                    }
                `,
            }),

            // form data
            zendesk_ticket_id: '',
        };
    },
    apollo: {
        schedulePause: {
            query: SchedulePause,
            variables() {
                return {
                    customer_id: _.toNumber(this.$route.params.customer_id),
                    property_id: _.toNumber(this.$route.params.property_id),
                    schedule_id: _.toNumber(this.$route.params.schedule_id),
                };
            },
            error(error) {
                lsErrorService.log({ error, showAlert: true });
                this.$apollo.queries.schedulePause.skip = true;
                lsHelpers.goToParentView({ replace: true });
            },
            loadingKey: 'loading',
            fetchPolicy: 'network-only',
        },
    },
    computed: {
        upcomingRange() {
            return lsHelpers.dateRange({
                start: this.schedulePause.next_customer_starts_at,
                end: this.schedulePause.next_customer_ends_at,
            });
        },
        reactivationRange() {
            return lsHelpers.dateRange({
                start: this.schedulePause.reactivation_starts_at,
                end: this.schedulePause.reactivation_ends_at,
            });
        },
    },
    methods: {
        async createSchedulePause() {
            const valid = await this.$validator.validateAll();
            this.isInvalid = !valid;
            if (!valid) {
                return;
            }

            this.isUpdating = true;
            this.$apollo
                .mutate({
                    mutation: CreateSchedulePause,
                    variables: {
                        customer_id: _.toNumber(this.$route.params.customer_id),
                        property_id: _.toNumber(this.$route.params.property_id),
                        schedule_id: _.toNumber(this.$route.params.schedule_id),
                        zendesk_ticket_id: this.zendesk_ticket_id
                            ? _.toInteger(this.zendesk_ticket_id)
                            : null,
                        initiator: 'admin',
                    },
                })
                .then(({ data }) => {
                    lsModalService.open({
                        parent: this,
                        component: FlowSuccessModal,
                        props: {
                            title: `Service paused for ${this.property.street}`,
                            copy: data.createSchedulePause.copy,
                            replaceRoute: true,
                        },
                    });
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isUpdating = false;
                });
        },
    },
};
</script>

<style lang="scss">
.ls-pause-view--info-card {
    font-weight: $semi-bold;
}
</style>
